import { pathOr } from 'ramda';
import { parse } from 'querystring';

import { isJourneyTimeRestricted } from 'utils/isJourneyTimeRestricted';
import Currency from 'utils/Currency';
import { sum } from 'utils/calculator';
import { isEROnly, buildSeniorsAges, isESOrERSegment, isERSegment } from 'utils/booking';
import { getMarginTariffCode, isTrainMarginApplicable } from 'utils/trainMargins';
import { searchTypes } from 'pages/BookingMagnet/BookingMagnet.constants';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { EVAC_ASSIST_TARIFF_CODE } from 'constants/tariffCodes';
import { types } from 'types/passengers';

export const buildSegmentItems = (segment, applyTrainMargins = false, segmentIsEROnly = false, isEvac = false) => {
  const items = segment.products.map(({ passengerId, tariffCode }) => ({
    passengerId,
    tariffCode
  }));

  const segmentIsESOrER = isESOrERSegment(segment);
  const segmentIsER = isERSegment(segment);

  if (segmentIsESOrER && applyTrainMargins) {
    const passengerId = 'passenger_1';
    const tariffCode = getMarginTariffCode({ isEROnly: segmentIsER });

    items.push({ passengerId, tariffCode });
  }

  if (isEvac) {
    const passengerId = 'passenger_1';
    const tariffCode = EVAC_ASSIST_TARIFF_CODE;

    items.push({ passengerId, tariffCode });
  }

  return items;
};

export const buildSegments = ({ outbound, inbound }, isReturnJourney, isPackageBooking = false, isEvac = false) => {
  const applyTrainMarginsOutbound = isTrainMarginApplicable({ outbound, isPackageBooking });

  const outboundIsEROnly = isEROnly({ outbound: outbound?.legs });
  const inboundIsEROnly = isEROnly({ inbound: inbound?.legs });

  const segments = outbound.legs.map((leg) => ({
    direction: 'outbound',
    origin: leg.origin.uic,
    destination: leg.destination.uic,
    startValidityDate: leg.timing.departureDate,
    serviceName: leg.serviceName,
    serviceIdentifier: leg.serviceIdentifier,
    items: buildSegmentItems(leg, applyTrainMarginsOutbound, outboundIsEROnly, isEvac)
  }));

  if (isReturnJourney) {
    const applyTrainMarginsInbound = isTrainMarginApplicable({ inbound, isPackageBooking });
    segments.push(
      ...inbound.legs.map((leg) => ({
        direction: 'inbound',
        origin: leg.origin.uic,
        destination: leg.destination.uic,
        startValidityDate: leg.timing.departureDate,
        serviceName: leg.serviceName,
        serviceIdentifier: leg.serviceIdentifier,
        items: buildSegmentItems(leg, applyTrainMarginsInbound, inboundIsEROnly, isEvac)
      }))
    );
  }

  return segments;
};

export const getTotalPrice = ({ outbound, inbound, fees = [] }) => {
  const reducer = (totalPrice, out) => {
    const passengerPrice = Currency.sum(out.products.map((item) => item.price));

    return totalPrice + passengerPrice;
  };

  const outboundTotalPrice = (outbound.legs && outbound.legs.reduce(reducer, 0)) || 0;
  const inboundTotalPrice = (inbound.legs && inbound.legs.reduce(reducer, 0)) || 0;

  return {
    totalPrice: Currency.sum([outboundTotalPrice, inboundTotalPrice, ...fees]),
    outboundTotalPrice,
    inboundTotalPrice
  };
};

export const hasProductFamilyMismatch = (requestedProductFamilies = [], returnedProductFamilies = []) => {
  if (returnedProductFamilies.length === 0 || requestedProductFamilies.length === 0) {
    return false;
  }

  if (returnedProductFamilies.length > 0) {
    return returnedProductFamilies.some((pf) => !requestedProductFamilies.includes(pf.familyGroup));
  }

  return true;
};

export const getSearchParams = (search, discountCards = []) => {
  const {
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    infants,
    youths,
    seniors,
    adultsWheelchair,
    childrenWheelchair,
    guideDogs,
    wheelchairCompanions,
    nonWheelchairCompanions,
    origin,
    destination,
    outboundDate,
    inboundDate,
    returningOrigin,
    returningDestination,
    contractCode,
    productFamilies,
    currency,
    subscriptionCode,
    bookingTypeSelected,
    maxTransfers,
    discountCard,
    seniors60To64,
    seniors65Plus,
    adultsEvac,
    childrenEvac,
    containSNCBStation,
    containDBStation,
    containNSStation,
    childAges,
    youthAges
  } = parse(search.substr(1));

  const filteredDiscountCards = discountCards
    ?.filter(({ code }) => code === discountCard)
    .map(({ code, name }) => ({
      code,
      name
    }));

  const seniorsAges = buildSeniorsAges({ seniors60To64, seniors65Plus });

  return {
    adults: Number(adults),
    adults12To15: Number(adults12To15),
    adults16Plus: Number(adults16Plus),
    adultsWheelchair: Number(adultsWheelchair),
    children: Number(children),
    children4Only: Number(children4Only),
    children5To11: Number(children5To11),
    childrenWheelchair: Number(childrenWheelchair),
    youths: Number(youths),
    seniors: Number(seniors),
    contractCode,
    currency,
    destination,
    guideDogs: Number(guideDogs),
    inboundDate,
    infants: Number(infants),
    nonWheelchairCompanions: Number(nonWheelchairCompanions),
    origin,
    outboundDate,
    productFamilies: productFamilies ? [productFamilies] : [],
    returningDestination,
    returningOrigin,
    wheelchairCompanions: Number(wheelchairCompanions),
    multipleFlexibility: true,
    isYouthAdultConversionActive: false,
    subscriptionCode,
    bookingTypeSelected,
    adultsEvac: Number(adultsEvac),
    containSNCBStation,
    containDBStation,
    containNSStation,
    childrenEvac: Number(childrenEvac),
    ...(discountCard && filteredDiscountCards?.length > 0 && { discountCards: filteredDiscountCards }),
    ...(maxTransfers && { maxTransfers: Number(maxTransfers) }),
    ...(seniorsAges.length > 0 && { seniorsAges }),
    ...(!!childAges && {
      childAges: childAges.split(', ')?.map((i) => Number(i))
    }),
    ...(!!youthAges && {
      youthAges: youthAges.split(', ')?.map((i) => Number(i))
    })
  };
};

export const buildOutboundJourneys = ({ data, outboundDate, inboundDate, selectedInbound }) =>
  pathOr([], ['journeySearch', 'outbound', 'journeys'], data).map((j) => ({
    ...j,
    isTimeRestricted: isJourneyTimeRestricted({
      outboundDate,
      outboundArrives: j.timing.arrives,
      inboundDate,
      inboundDeparts: selectedInbound && selectedInbound.legs[0].timing.departs
    })
  }));

export const buildInboundJourneys = ({ data, outboundDate, inboundDate, selectedOutbound }) =>
  pathOr([], ['journeySearch', 'inbound', 'journeys'], data).map((j) => ({
    ...j,
    isTimeRestricted: isJourneyTimeRestricted({
      outboundDate,
      outboundArrives: selectedOutbound && selectedOutbound.legs[selectedOutbound.legs.length - 1].timing.arrives,
      inboundDate,
      inboundDeparts: j.timing.departs
    })
  }));

export const buildLegFromFare = (selectedFare, i = 0) =>
  selectedFare
    ? {
        bookingItems: selectedFare.bookingItems.map(({ tariffCode, ...others }) => ({
          tariffDetails: { tariffCode },
          productFamily: selectedFare.productFamily,
          ...others
        })),
        legs: selectedFare.legs[i],
        origin: selectedFare.legs[i].origin,
        destination: selectedFare.legs[i].destination,
        date: selectedFare.legs[i].timing.departureDate,
        arrivalTime: selectedFare.legs[i].timing.arrives,
        departureTime: selectedFare.legs[i].timing.departs,
        classOfService: selectedFare.legs[i].classOfService,
        productFamily: selectedFare.productFamily
      }
    : null;

export const buildLegsFromFare = (selectedFare) => {
  const fares = [];

  if (selectedFare) {
    for (let i = 0; i < selectedFare.legs.length; i += 1) {
      fares.push(buildLegFromFare(selectedFare, i));
    }
  }

  return fares;
};

export const buildInboundInfo = ({
  date,
  returningOriginStationName,
  returningDestinationStationName,
  destinationStationName,
  originStationName,
  originUIC,
  destinationUIC,
  returningOriginUIC,
  returningDestinationUIC
}) => {
  if (!date) {
    return null;
  }

  const isMultiCityJourney = Boolean(returningOriginStationName && returningDestinationStationName);
  const origin = isMultiCityJourney ? returningOriginStationName : destinationStationName;
  const destination = isMultiCityJourney ? returningDestinationStationName : originStationName;

  const inboundOriginUIC = isMultiCityJourney ? returningOriginUIC : destinationUIC;
  const inboundDestinationUIC = isMultiCityJourney ? returningDestinationUIC : originUIC;

  return {
    date,
    origin,
    destination,
    originUIC: inboundOriginUIC,
    destinationUIC: inboundDestinationUIC
  };
};

export const calcSegmentPointsTotal = (segment) =>
  segment?.legs?.reduce((acc, leg) => {
    const points = leg.products.map((product) => product.loyaltyPoints);

    return sum([acc, ...points]);
  }, 0);

export const getTotalAddonPrice = ({ outbound, inbound }) => {
  const reducer = (prices, acc) => {
    const addonPrice = acc?.addOnLeg ? Currency.sum(acc?.addOnLeg?.products?.map((item) => item?.price)) : 0;

    return prices + addonPrice;
  };

  const outboundTotalAddOnPrice = outbound?.reduce(reducer, 0) || 0;
  const inboundTotalAddOnPrice = inbound?.reduce(reducer, 0) || 0;

  return {
    outboundTotalAddOnPrice,
    inboundTotalAddOnPrice
  };
};

export const getAvailableTrainsForJourney = ({ travels }) => [
  ...new Set(
    travels.flatMap(({ fares }) =>
      fares
        ?.filter(({ bookingItems, buckets, canBeBooked }) => bookingItems.length > 0 && buckets.length > 0 && canBeBooked)
        ?.flatMap(({ legs }) => legs?.flatMap(({ serviceName }) => serviceName))
    )
  )
];

export const getAvailableTrainsESForJourney = ({ travels }) => [
  ...new Set(
    travels.flatMap(({ fares }) =>
      fares
        ?.filter(({ bookingItems, buckets, canBeBooked }) => bookingItems.length > 0 && buckets.length > 0 && canBeBooked)
        ?.flatMap(({ legs }) =>
          legs?.filter(({ serviceType }) => serviceType?.code === serviceTypeCodes.EUROSTAR)?.flatMap(({ serviceName }) => serviceName)
        )
    )
  )
];

export const isDogOnlySearch = ({ searchParams }) => {
  if (searchParams.bookingTypeSelected !== searchTypes.WITH_DISABILITY) return false;

  const {
    adultsWheelchair,
    childrenWheelchair,
    wheelchairCompanions,
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    guideDogs,
    infants,
    nonWheelchairCompanions,
    seniors,
    youths
  } = searchParams;
  const passengersFromSearch = [
    { type: 'adultsWheelchair', value: adultsWheelchair },
    { type: 'childrenWheelchair', value: childrenWheelchair },
    { type: 'wheelchairCompanions', value: wheelchairCompanions },
    { type: 'adults', value: adults },
    { type: 'adults12To15', value: adults12To15 },
    { type: 'adults16Plus', value: adults16Plus },
    { type: 'children', value: children },
    { type: 'children4Only', value: children4Only },
    { type: 'children5To11', value: children5To11 },
    { type: 'guideDogs', value: guideDogs },
    { type: 'infants', value: infants },
    { type: 'nonWheelchairCompanions', value: nonWheelchairCompanions },
    { type: 'seniors', value: seniors },
    { type: 'youths', value: youths }
  ];

  const hasDogsPaxs = passengersFromSearch.filter(({ type }) => type === 'guideDogs').some(({ value }) => value > 0);
  const hasOtherPaxs = passengersFromSearch.filter(({ type }) => type !== 'guideDogs').some(({ value }) => value > 0);

  return hasDogsPaxs && !hasOtherPaxs;
};

/**
 * https://eurostar.atlassian.net/browse/VOY-4639
 *
 * For now consider the following:
 * Adult is 31 years old
 * Youth is 16 years old
 * Child is 11 years old
 * Senior is 61 years old
 */
export const passengerTypeToAgeMapping = {
  AD: 31,
  YTH: 16,
  CH: 11,
  SNR: 61
};

export const isSNCBSummatedFare = ({ journey = {} }) =>
  !!journey?.isSummatedFare && journey?.legs?.some((leg) => leg.serviceType?.code === serviceTypeCodes.SNCB);

export const buildAgeForSNCBDBOrNS = ({ id, type, seniorsAges = [], youthAges = [], childAges = [] }) => {
  const passengerTypes = [types.ADULT, types.YOUTH, types.CHILD, types.SENIOR];

  if (passengerTypes.includes(type)) {
    if (type === types.SENIOR && seniorsAges.length > 0) {
      return seniorsAges.find((senior) => id === senior.id)?.age;
    }

    if (type === types.YOUTH && youthAges.length > 0) {
      return youthAges.find((youth) => id === youth.id)?.age;
    }

    if (type === types.CHILD && childAges.length > 0) {
      return childAges.find((child) => id === child.id)?.age;
    }

    return passengerTypeToAgeMapping[type];
  }

  return passengerTypeToAgeMapping[types.ADULT];
};

export const sendSNCBDBOrNSAge = ({
  id,
  type,
  containSNCBStation = 'false',
  containDBStation = 'false',
  containNSStation = 'false',
  outbound = {},
  inbound = {},
  seniorsAges = [],
  youthAges = [],
  childAges = []
}) => {
  const legs = [...(outbound?.legs ?? []), ...(inbound?.legs ?? [])];
  const doesnotHaveAnSNCFConnection = legs.every(({ serviceType }) => serviceType?.code !== serviceTypeCodes.SNCF);

  if (
    (containSNCBStation?.toString() === 'true' || containDBStation?.toString() === 'true' || containNSStation?.toString() === 'true') &&
    doesnotHaveAnSNCFConnection
  )
    return buildAgeForSNCBDBOrNS({ id, type, seniorsAges, youthAges, childAges });
  return null;
};
